import {
  AU_STATE_CODES,
  GLOBAL_LISTING_TYPE_OPTIONS,
  GLOBAL_PROPERTY_TYPE_OPTIONS,
  GLOBAL_TIME_RANGE_OPTIONS,
  GlobalCrawlConfigSchema,
} from "@repo/adapter-core";
import { z } from "zod";

const values = <T extends readonly { value: string }[]>(options: T) =>
  options.map((o) => o.value) as [T[number]["value"], ...T[number]["value"][]];

export const PROVIDER_MODES = ["auto", "fallback", "forced"] as const;
export const SCRAPE_PROVIDERS = ["firecrawl_scrape", "firecrawl_agent", "zyte", "direct"] as const;

/**
 * Crawl config as the AI path may write it: the shared GlobalCrawlConfigSchema plus the
 * provider fields the wizard already stores (AdapterRuntimeConfig / CrawlJobConfig), with
 * enums tightened. Kept separate from GlobalCrawlConfigSchema on purpose: adapters parse
 * stored job configs with that schema, and stored configs carry the UI sentinel
 * `provider: "any"`, which a tightened base schema would reject at run time.
 * sortBy/manualUrl/rollingFirstRunDays are omitted — they are not something to infer from prose.
 */
export const JobCrawlConfigSchema = GlobalCrawlConfigSchema.omit({ sortBy: true, manualUrl: true, rollingFirstRunDays: true })
  .extend({
    timeRange: z.enum(values(GLOBAL_TIME_RANGE_OPTIONS)).optional(),
    propertyTypes: z.array(z.enum(values(GLOBAL_PROPERTY_TYPE_OPTIONS))).optional(),
    providerMode: z.enum(PROVIDER_MODES).optional(),
    provider: z.enum(SCRAPE_PROVIDERS).optional(),
  })
  .strict();

export type JobCrawlConfig = z.infer<typeof JobCrawlConfigSchema>;

/** Defaults the system prompt states and the model records in assumedDefaults when used. */
export const JOB_CONFIG_DEFAULTS = {
  listingType: "sold",
  dateFilterMode: "relative",
  timeRange: "last-90-days",
  providerMode: "auto",
  excludeSurroundingSuburbs: true,
  excludePriceWithheld: false,
  runType: "immediate",
} as const;

/** Global fields that always carry a value (the default when the user is silent). */
const REQUIRED_GLOBAL_FIELDS = [
  "listingType",
  "dateFilterMode",
  "excludeSurroundingSuburbs",
  "excludePriceWithheld",
  "providerMode",
] as const;

type JsonSchema = Record<string, unknown>;

function field(type: string, nullable: boolean, extra: JsonSchema = {}): JsonSchema {
  const schema: JsonSchema = { type: nullable ? [type, "null"] : type, ...extra };
  if (Array.isArray(extra.enum) && nullable) schema.enum = [...extra.enum, null];
  return schema;
}

function strictObject(properties: Record<string, JsonSchema>, description?: string): JsonSchema {
  return {
    type: "object",
    ...(description ? { description } : {}),
    properties,
    required: Object.keys(properties),
    additionalProperties: false,
  };
}

function crawlConfigJsonSchema(kind: "global" | "override"): JsonSchema {
  const isRequired = (name: string) =>
    kind === "global" && (REQUIRED_GLOBAL_FIELDS as readonly string[]).includes(name);
  const f = (name: string, type: string, extra: JsonSchema = {}) =>
    field(type, !isRequired(name), extra);
  const int = (description: string) => ({ description });

  return strictObject(
    {
      listingType: f("listingType", "string", { enum: values(GLOBAL_LISTING_TYPE_OPTIONS) }),
      propertyTypes: field("array", true, {
        description: "null = all property types",
        items: { type: "string", enum: values(GLOBAL_PROPERTY_TYPE_OPTIONS) },
      }),
      // The date window is job-wide (one per job), so overrides cannot set it.
      ...(kind === "global"
        ? {
            dateFilterMode: f("dateFilterMode", "string", { enum: ["relative", "absolute"] }),
            timeRange: f("timeRange", "string", {
              description: "relative mode only",
              enum: values(GLOBAL_TIME_RANGE_OPTIONS),
            }),
            dateFrom: f("dateFrom", "string", { description: "absolute mode only, YYYY-MM-DD" }),
            dateTo: f("dateTo", "string", { description: "absolute mode only, YYYY-MM-DD" }),
          }
        : {}),
      minPrice: f("minPrice", "integer", int("AUD")),
      maxPrice: f("maxPrice", "integer", int("AUD")),
      minBedrooms: f("minBedrooms", "integer", int("bedrooms")),
      maxBedrooms: f("maxBedrooms", "integer", int("bedrooms")),
      minBathrooms: f("minBathrooms", "integer", int("bathrooms")),
      minParking: f("minParking", "integer", int("car spaces")),
      excludeSurroundingSuburbs: f("excludeSurroundingSuburbs", "boolean"),
      excludePriceWithheld: f("excludePriceWithheld", "boolean"),
      providerMode: f("providerMode", "string", { enum: [...PROVIDER_MODES] }),
      provider: f("provider", "string", {
        description: "only with providerMode forced",
        enum: [...SCRAPE_PROVIDERS],
      }),
    },
    kind === "override" ? "null = inherit the global value" : undefined,
  );
}

/** Strict JSON schema for OpenRouter `response_format: { type: "json_schema", strict: true }`. */
export function buildJobConfigJsonSchema(adapterSlugs: readonly string[]): JsonSchema {
  const slugEnum = { type: "string", enum: [...adapterSlugs] };
  const text = { type: "string" };
  return strictObject({
    jobName: text,
    runType: { type: "string", enum: ["immediate", "scheduled"] },
    cronExpression: field("string", true, {
      description: "5-field cron, only when runType is scheduled",
    }),
    suburbs: {
      type: "array",
      items: strictObject({
        locality: text,
        postcode: field("string", true, { description: "only if the user gave it" }),
        state: field("string", true, {
          description: "only if the user gave it",
          enum: [...AU_STATE_CODES],
        }),
      }),
    },
    suburbListNames: { type: "array", items: text },
    adapterSlugs: { type: "array", items: slugEnum },
    globalConfig: crawlConfigJsonSchema("global"),
    adapterOverrides: {
      type: "array",
      items: strictObject({ adapterSlug: slugEnum, config: crawlConfigJsonSchema("override") }),
    },
    assumedDefaults: {
      type: "array",
      items: strictObject({ field: text, value: text, reason: text }),
    },
    unresolved: { type: "array", items: strictObject({ field: text, question: text }) },
  });
}

export interface SuburbRequest {
  locality: string;
  postcode: string | null;
  state: string | null;
}

export interface AssumedDefault {
  field: string;
  value: string;
  reason: string;
}

export interface UnresolvedItem {
  field: string;
  question: string;
}

export interface ParsedJobConfig {
  jobName: string;
  runType: "immediate" | "scheduled";
  cronExpression: string | null;
  suburbs: SuburbRequest[];
  suburbListNames: string[];
  adapterSlugs: string[];
  globalConfig: JobCrawlConfig;
  adapterOverrides: { adapterSlug: string; config: JobCrawlConfig }[];
  assumedDefaults: AssumedDefault[];
  unresolved: UnresolvedItem[];
}

const OutputEnvelopeSchema = z
  .object({
    jobName: z.string().trim().min(1),
    runType: z.enum(["immediate", "scheduled"]),
    cronExpression: z.string().nullable(),
    suburbs: z.array(
      z
        .object({
          locality: z.string().trim().min(1),
          postcode: z
            .string()
            .regex(/^\d{4}$/)
            .nullable(),
          state: z.enum(AU_STATE_CODES).nullable(),
        })
        .strict(),
    ),
    suburbListNames: z.array(z.string().trim().min(1)),
    adapterSlugs: z.array(z.string()),
    globalConfig: z.record(z.unknown()),
    adapterOverrides: z.array(
      z.object({ adapterSlug: z.string(), config: z.record(z.unknown()) }).strict(),
    ),
    assumedDefaults: z.array(
      z.object({ field: z.string(), value: z.string(), reason: z.string() }).strict(),
    ),
    unresolved: z.array(z.object({ field: z.string(), question: z.string().min(1) }).strict()),
  })
  .strict();

const CRON_RE = /^\s*[\d*/,-]+(\s+[\d*/,-]+){4}\s*$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function withoutNulls(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== null));
}

function formatIssues(prefix: string, error: z.ZodError): string[] {
  return error.issues.map(
    (i) => `${prefix}${i.path.length ? `.${i.path.join(".")}` : ""}: ${i.message}`,
  );
}

function isIsoDate(value: string): boolean {
  return ISO_DATE_RE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

/** Cross-field rules zod cannot express on one object. `merged` relaxes relative-mode checks. */
function crawlConfigErrors(path: string, config: JobCrawlConfig, merged = false): string[] {
  const errors: string[] = [];
  if (config.dateFilterMode === "absolute") {
    if (!config.dateFrom || !config.dateTo) {
      errors.push(`${path}: absolute date mode needs dateFrom and dateTo`);
    } else if (!isIsoDate(config.dateFrom) || !isIsoDate(config.dateTo)) {
      errors.push(`${path}: dates must be YYYY-MM-DD`);
    } else if (config.dateFrom > config.dateTo) {
      errors.push(`${path}: dateFrom is after dateTo`);
    }
    if (!merged && config.timeRange) errors.push(`${path}: timeRange is for relative mode only`);
  }
  if (config.dateFilterMode === "relative") {
    if (!config.timeRange) errors.push(`${path}: relative date mode needs timeRange`);
    if (!merged && (config.dateFrom || config.dateTo)) {
      errors.push(`${path}: dateFrom/dateTo are for absolute mode only`);
    }
  }
  if (config.providerMode === "forced" && !config.provider) {
    errors.push(`${path}: providerMode forced needs a provider`);
  }
  if (config.provider && config.providerMode !== "forced") {
    errors.push(`${path}: provider is only allowed with providerMode forced`);
  }
  for (const [min, max] of [
    ["minPrice", "maxPrice"],
    ["minBedrooms", "maxBedrooms"],
  ] as const) {
    const lo = config[min];
    const hi = config[max];
    if (lo !== undefined && hi !== undefined && lo > hi) errors.push(`${path}: ${min} > ${max}`);
  }
  return errors;
}

/**
 * Validate the model's structured output. Rejects rather than coerces: unknown keys, values
 * outside the enums, contradictory date/provider settings and foreign adapter slugs all fail.
 */
export function parseJobConfigOutput(
  raw: unknown,
  adapterSlugs: readonly string[],
): { ok: true; value: ParsedJobConfig } | { ok: false; errors: string[] } {
  const envelope = OutputEnvelopeSchema.safeParse(raw);
  if (!envelope.success) return { ok: false, errors: formatIssues("output", envelope.error) };
  const out = envelope.data;
  const errors: string[] = [];

  const unknownSlugs = [
    ...out.adapterSlugs,
    ...out.adapterOverrides.map((o) => o.adapterSlug),
  ].filter((slug) => !adapterSlugs.includes(slug));
  if (unknownSlugs.length) errors.push(`unknown adapter slugs: ${unknownSlugs.join(", ")}`);

  if (out.runType === "scheduled" && !(out.cronExpression && CRON_RE.test(out.cronExpression))) {
    errors.push("cronExpression: scheduled jobs need a 5-field cron expression");
  }
  if (out.runType === "immediate" && out.cronExpression !== null) {
    errors.push("cronExpression: must be null for immediate jobs");
  }

  const global = JobCrawlConfigSchema.safeParse(withoutNulls(out.globalConfig));
  if (!global.success) errors.push(...formatIssues("globalConfig", global.error));
  const globalConfig = global.success ? global.data : {};
  if (global.success) {
    for (const name of REQUIRED_GLOBAL_FIELDS) {
      if (globalConfig[name] === undefined) errors.push(`globalConfig.${name}: required`);
    }
    errors.push(...crawlConfigErrors("globalConfig", globalConfig));
  }

  const adapterOverrides: ParsedJobConfig["adapterOverrides"] = [];
  const seen = new Set<string>();
  for (const [index, override] of out.adapterOverrides.entries()) {
    const path = `adapterOverrides.${index}`;
    if (!out.adapterSlugs.includes(override.adapterSlug)) {
      errors.push(`${path}: ${override.adapterSlug} is not in adapterSlugs`);
    }
    if (seen.has(override.adapterSlug)) errors.push(`${path}: duplicate ${override.adapterSlug}`);
    seen.add(override.adapterSlug);
    const parsed = JobCrawlConfigSchema.safeParse(withoutNulls(override.config));
    if (!parsed.success) {
      errors.push(...formatIssues(`${path}.config`, parsed.error));
      continue;
    }
    if (Object.keys(parsed.data).length === 0) continue;
    errors.push(...crawlConfigErrors(path, { ...globalConfig, ...parsed.data }, true));
    adapterOverrides.push({ adapterSlug: override.adapterSlug, config: parsed.data });
  }

  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    value: {
      ...out,
      cronExpression: out.cronExpression?.trim() ?? null,
      adapterSlugs: [...new Set(out.adapterSlugs)],
      globalConfig,
      adapterOverrides,
    },
  };
}
